/**
 * The rules about a vendor's *shares* — the deposit and retentions taken out of what a request comes
 * to — in one place, so the cell that flags a figure and the schema that refuses the submit can't
 * disagree about it.
 *
 * A plain utility rather than part of `use-vendor-payment-request`: the validation schemas import
 * these, and a schema reaching into a component composable would tie validation to the view layer.
 * Nothing here knows about a flow, a form or a component — it takes numbers and lines, and answers.
 */

import type { BOQItem } from '@/types/api/sites.types';

/**
 * Whole percent, defined once. Both the inputs' `max` and the schema's refusal read it, and a second
 * copy drifting from this one is how a user gets a figure the control accepted and the submit
 * rejected, with no explanation on screen.
 */
export const FULL_SHARE_PERCENT = 100;

/**
 * A share has to be a real slice of the request: something, and not the whole of it — the whole
 * would leave a balance request for nothing.
 */
export const isUsableShare = (percent?: number | null) =>
  typeof percent === 'number' && percent > 0 && percent < FULL_SHARE_PERCENT;

/** 0.01% to 99.99%: the platform's documented deposit range, in every create flow. */
export const DEPOSIT_PERCENT_MIN = 0.01;
export const DEPOSIT_PERCENT_MAX = 99.99;

export const isUsableDepositPercent = (percent?: number | null): percent is number =>
  typeof percent === 'number' &&
  !Number.isNaN(percent) &&
  percent >= DEPOSIT_PERCENT_MIN &&
  percent <= DEPOSIT_PERCENT_MAX;

/** The vendors a set of lines is actually bought from — blank ids are lines with no vendor yet. */
export const vendorIdsForLines = (items: BOQItem[] | undefined) => [
  ...new Set((items ?? []).map((line) => line.vendorId).filter(Boolean)),
];

/**
 * What one vendor is holding back in total, deposit and retentions together.
 *
 * Its own function because the individual rules can't catch what it catches: each share can be a
 * legitimate slice and still leave nothing to pay between them.
 */
/**
 * Whether an item contra charge is priced — both figures real.
 *
 * Context: a charge is `quantity × rate`, so a zero in either makes it a deduction that deducts
 * nothing. Shared because four places ask it: the dialog refuses to add one, the Confirm step flags
 * the row, the schema refuses the submit, and the store throws before anything is created. Four
 * copies of one rule drift — the dialog would accept a charge the step flags, or the store would
 * throw on one both had accepted.
 */
export const isPricedItemContraCharge = (charge: {
  quantity?: number | null;
  rate?: number | null;
}) => (charge.quantity ?? 0) > 0 && (charge.rate ?? 0) > 0;

export const withheldPercent = (selection?: {
  deposit?: { percent?: number | null };
  retentions?: { percent?: number | null }[];
}) =>
  (selection?.deposit?.percent ?? 0) +
  (selection?.retentions ?? []).reduce((sum, retention) => sum + (retention.percent ?? 0), 0);

/**
 * What a deposit's or retention's percentage comes to, in whole cents.
 *
 * Rounded to the nearest cent. The balance is then whatever is left rather than a second percentage
 * of the total, so the two payment requests always sum to exactly what the vendor is owed.
 *
 * Context: for a deposit this is the figure of record, not a preview. Every create flow sends the
 * deposit to the API as this amount and never as its percentage, so the figure on screen is the one
 * requested and the rounding happens here once (PE-882, 2026-09-25).
 *
 * The `toFixed` is not decoration: binary floating point puts values like 87 × 10% at
 * 869.9999999999999 cents, and rounding what the arithmetic actually produced lands a cent out.
 */
export const shareAmountFor = (total: number, percent?: number | null): number => {
  if (typeof percent !== 'number' || percent <= 0 || total <= 0) return 0;

  const amount = Math.round(Number((((total * percent) / 100) * 100).toFixed(4))) / 100;

  // A share *short of* the whole order has to leave something for the balance request, and 99.99% of
  // a small order rounds to the whole of it — so those are held a cent below the total.
  //
  // 100% is not: there is no balance to protect, because the schema refuses the whole order as a
  // deposit outright. Shaving the cent off it anyway invented a R0.01 balance request out of a
  // figure the user hadn't asked for, and left the amount they typed disagreeing with the total
  // beside it — R50.00 and R0.01 against a R50.00 order.
  if (percent >= 100) return amount;

  return Math.min(amount, Math.round(total * 100 - 1) / 100);
};

/**
 * What one vendor's lines come to, VAT included.
 *
 * `totalInclVat` rather than `total`: the figure every deduction is judged against is the one the
 * inventory footer and the Confirm step's vendor header both show, and mixing the VAT-exclusive
 * field in is what makes a total fail to reconcile with the rows above it.
 */
export const vendorLinesTotal = (items: BOQItem[] | undefined, vendorId: string) =>
  (items ?? [])
    .filter((line) => line.vendorId === vendorId)
    .reduce((sum, line) => sum + (line.totalInclVat ?? line.total ?? 0), 0);

/**
 * The amount one vendor's deposit is sent as, or `null` where it has none: the figure the Confirm
 * step shows for it, worked out against the same total as that step's vendor header.
 */
export const vendorDepositAmount = (
  items: BOQItem[] | undefined,
  vendorId: string,
  percent: number | null | undefined,
): number | null =>
  typeof percent === 'number' ? shareAmountFor(vendorLinesTotal(items, vendorId), percent) : null;

/**
 * What a vendor's contra charges come to, both units together.
 *
 * Its own function because three places need the same answer — the step's rows, the schema's
 * ceiling, and the combined-deduction rule — and two roundings of one figure is how a total stops
 * reconciling with the rows above it.
 */
export const contraChargesTotal = (charges?: {
  valueContraCharges?: { amount?: number | null }[];
  itemContraCharges?: { quantity?: number | null; rate?: number | null }[];
}) =>
  (charges?.valueContraCharges ?? []).reduce((sum, charge) => sum + (charge.amount ?? 0), 0) +
  (charges?.itemContraCharges ?? []).reduce(
    (sum, charge) => sum + (charge.quantity ?? 0) * (charge.rate ?? 0),
    0,
  );

/**
 * Everything held back from one vendor's claim, as money.
 *
 * Context: the percentage rule beside this one (`withheldPercent`) cannot see a contra charge,
 * because a contra charge is a figure rather than a share — so a claim could be emptied by
 * deductions that each passed their own rule. This converts the shares to money and adds the
 * charges, which is the only basis on which the four can be judged together.
 */
export const withheldAmount = (
  claimTotal: number,
  selection?: { deposit?: { percent?: number | null }; retentions?: { percent?: number | null }[] },
  charges?: Parameters<typeof contraChargesTotal>[0],
) =>
  shareAmountFor(claimTotal, selection?.deposit?.percent) +
  (selection?.retentions ?? []).reduce(
    (sum, retention) => sum + shareAmountFor(claimTotal, retention.percent),
    0,
  ) +
  contraChargesTotal(charges);
